import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Popconfirm,
  Row,
  Skeleton,
  Table,
  Modal,
  notification,
  Form,
} from 'antd';
import { useParams, useSearchParams } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { DeleteOutlined } from '@ant-design/icons';
import { useIsAuthenticated } from '@azure/msal-react';
import EventForm from './ProceedingTypeForm';
import { config } from '../../config';
import { DELETE_PROCEEDING_EVENT, GET_ALL_EVENTS } from './ProceedingTypeConstants';
import CustomTooltip from '../CustomComponents/CustomTooltip';

const ProceedingEvents: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const { proceedingId } = useParams<any>();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [form] = Form.useForm();
  const applicationNumberTooltipDescription =
    'For more information on an application(s) click on the application number(s) below.';

  const [deleteProcEvent, { loading: deleteing, error: deleteError }] =
    useMutation(DELETE_PROCEEDING_EVENT);
  const [getAllEvents, { data: allEvents, loading, error, refetch }] = useLazyQuery(GET_ALL_EVENTS);

  useEffect(() => {
    if (tab === '2' && proceedingId) {
      getAllEvents({
        variables: {
          input: proceedingId,
        },
      });
    }
  }, [tab, proceedingId]);

  const deleteProceedingEvent = async (event: any, e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteProcEvent({
      variables: {
        input: {
          proceedingId: proceedingId,
          proceedingEventId: event.id,
          source: event.source,
          type: event.type,
          typeNumber: event.typeNumber,
        },
      },
    });
    refetch();
  };

  const handleModalOpen = (eventId?: string) => {
    setSelectedEventId(eventId || null);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedEventId(null);
    form.resetFields();
  };

  const handleEventFormSubmit = (success: boolean) => {
    if (success) {
      refetch();
      notification.success({
        message: 'Success',
        description: selectedEventId ? 'Proceeding Type updated' : 'Type added to Proceeding',
      });
    }
    setIsModalVisible(false);
  };

  const events = allEvents?.proceedingEvents?.data.map((proceeding: any) => ({
    ...proceeding,
    key: proceeding.id,
  }));

  const CustomTitle = () => (
    <div style={{ textAlign: 'left' }}>
      <div>Name of Applicant or Requestor</div>
      <div>or Approval Holder</div>
    </div>
  );

  const columns: any[] = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
    },
    {
      title: <CustomTitle />,
      dataIndex: 'applicantName',
      key: 'applicantName',
      width: 250,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 250,
    },
    {
      title: (
        <>
          Number
          <CustomTooltip title={applicationNumberTooltipDescription}></CustomTooltip>
        </>
      ),
      dataIndex: 'typeNumber',
      key: 'typeNumber',
      width: 120,
      render: (_: unknown, event: any) => {
        const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
          e.stopPropagation();
        };
        if (event.type === 'Application' && event.source === 'OneStop 1.0') {
          return (
            <a
              href={`${config.REACT_APP_APP_QUERY_URL}${event.typeNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClick}
            >
              {event.typeNumber}
            </a>
          );
        } else if (event.type === 'Application' && event.source === 'IAR') {
          return (
            <a
              href={`${config.REACT_APP_IAR_URL}=${event.typeNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClick}
            >
              {event.typeNumber}
            </a>
          );
        } else {
          return event?.typeNumber;
        }
      },
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 120,
    },
  ];
  if (isAuthenticated) {
    columns.push({
      title: 'Delete',
      key: 'deleteIcon',
      width: 50,
      render: (_: any, event: any) => {
        return isAuthenticated ? (
          <div className="ml-1 inline-block">
            <Popconfirm
              title="Delete Event"
              description={'Are you sure to delete this proceeding type?'}
              onConfirm={(e) => deleteProceedingEvent(event, e!)}
              onCancel={(e) => e.stopPropagation()}
              okText="Yes"
              cancelText="No"
            >
              <Button
                icon={<DeleteOutlined />}
                size={'small'}
                style={{ backgroundColor: 'white', color: 'red' }}
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          </div>
        ) : (
          <></>
        );
      },
    });
  }

  const getFormTitle = () => {
    if (isAuthenticated) {
      return (selectedEventId !== null ? 'Edit' : 'Add') + ' Proceeding Type';
    }
    return 'View Proceeding Type';
  };

  return (
    <>
      {loading || deleteing ? (
        <Skeleton />
      ) : (
        <div style={{ margin: '0 20px', minHeight: '100vh', marginTop: '10px' }}>
          <Row>
            {isAuthenticated && (
              <Button
                type="primary"
                onClick={() => handleModalOpen()}
                style={{ marginLeft: '10px' }}
              >
                Add Proceeding Type
              </Button>
            )}
          </Row>
          <Row>
            <Card
              title={<span className="highlighted-title">Proceeding Types</span>}
              style={{ width: '100%', marginTop: '25px' }}
              className="card"
            >
              <Table
                dataSource={events}
                loading={loading}
                columns={columns}
                tableLayout="fixed"
                onRow={(record: any) => ({
                  onClick: () => handleModalOpen(record.id),
                  style: { cursor: 'pointer' },
                })}
                rowClassName="clickable-row"
              />
            </Card>
          </Row>
          <Modal
            title={getFormTitle()}
            open={isModalVisible}
            onCancel={handleModalClose}
            footer={null}
            destroyOnClose
          >
            <EventForm
              onSubmit={handleEventFormSubmit}
              onCancel={handleModalClose}
              eventId={selectedEventId}
              proceedingId={proceedingId}
              form={form}
            />
          </Modal>
        </div>
      )}
    </>
  );
};

export default ProceedingEvents;
